import { ObservableMap } from '@stencil/store';
import { ServiceType } from '@uipath/portal-shell-util';

import { Scope } from '../constants/constants';
import { SERVICE_IDS } from '../constants/defaultServiceOrder';
import { MenuConfig } from '../models/menuConfig';
import { Service } from '../models/service';
import { State } from '../store/types/state';
import { isMsiOrStandalone } from './EnvironmentUtil';
import { getLanguage } from './languageHelper';
import { t } from './localization/loc';
import Logger from './logger/logger';

export function getScopedUrl(
    organizationStandardUrl: string,
    tenantName: string,
    serviceType: ServiceType | 'identity',
    scope: Scope,
    isStandAlone?: boolean,
) {
    return isStandAlone
        ? `${organizationStandardUrl}/${serviceType}`
        : scope === Scope.Global
            ? `${organizationStandardUrl}/${serviceType}_`
            : `${organizationStandardUrl}/${tenantName}/${serviceType}_`;
}

export function getMenuConfig(store: ObservableMap<State>): MenuConfig {
    const locale = getLanguage(store);
    const isStandAlone = !!isMsiOrStandalone(store);
    const isHost = store.get('isHost');

    const baseUrl = store.get('baseUrl');
    const accountName = store.get('accountName');
    const organizationStandardUrl = store.get('organizationStandardUrl');
    const tenantName = store.get('currentTenant');
    const active = store.get('active');

    const standardUrl = isStandAlone ? baseUrl : organizationStandardUrl || `${baseUrl}/${accountName}`;

    Logger.info(
        'menu',
        `Base Url: ${baseUrl}, accountName: ${accountName}, tenantName: ${tenantName}, active: ${active}, locale: ${locale}, isStandAlone: ${isStandAlone}, isHost: ${isHost}`,
    );

    return {
        services: [
            {
                id: SERVICE_IDS.home,
                name: t('home', locale),
                svg: 'home',
                scope: Scope.Global,
                serviceType: ServiceType.cloudRpa,
                url: `${getScopedUrl(standardUrl, tenantName, ServiceType.cloudRpa, Scope.Global, isStandAlone)}/home`,
                isLongNameProduct: [ 'ru' ],
            },
            {
                id: SERVICE_IDS.automationhub,
                name: 'Automation Hub',
                svg: 'automationhub',
                scope: Scope.Tenant,
                serviceType: ServiceType.automationHub,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.automationHub, Scope.Tenant, isStandAlone),
                isLongNameProduct: true,
                docId: 'automation-hub',
            },
            {
                id: SERVICE_IDS.actions,
                name: 'Actions',
                svg: 'actions',
                scope: Scope.Tenant,
                serviceType: ServiceType.actions,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.actions, Scope.Tenant, isStandAlone),
                docId: 'action-center',
            },
            {
                id: SERVICE_IDS.apps,
                name: 'Apps',
                svg: 'apps',
                scope: Scope.Global,
                serviceType: ServiceType.apps,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.apps, Scope.Global, isStandAlone),
                docId: 'apps',
            },
            {
                id: SERVICE_IDS.chatbots,
                name: 'Chatbots',
                svg: 'chatbots',
                scope: Scope.Tenant,
                serviceType: ServiceType.chatBots,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.chatBots, Scope.Tenant, isStandAlone),
                docId: 'chatbots',
            },
            {
                id: SERVICE_IDS.entities,
                name: 'Entities',
                svg: 'entities',
                scope: Scope.Tenant,
                serviceType: ServiceType.entities,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.entities, Scope.Tenant, isStandAlone),
            },
            {
                id: SERVICE_IDS.orchestrator,
                name: 'Orchestrator',
                svg: 'orchestrator',
                scope: Scope.Tenant,
                serviceType: ServiceType.orchestrator,
                url: getScopedUrl(
                    standardUrl,
                    tenantName,
                    ServiceType.orchestrator,
                    isHost ? Scope.Global : Scope.Tenant, // When host mode do not add tenant name to URL
                    isStandAlone,
                ),
                showInHostAccount: true,
                isLongNameProduct: true,
                docId: 'orchestrator',
            },
            {
                id: SERVICE_IDS.processes,
                name: 'Processes',
                svg: 'processes',
                scope: Scope.Tenant,
                serviceType: ServiceType.processes,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.processes, Scope.Tenant, isStandAlone),
            },
            {
                id: SERVICE_IDS.insights,
                name: 'Insights',
                svg: 'insights',
                scope: Scope.Tenant,
                serviceType: ServiceType.insights,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.insights, Scope.Tenant, isStandAlone),
                docId: 'insights',
            },
            {
                id: SERVICE_IDS.marketplace,
                name: 'Marketplace',
                svg: 'marketplace',
                scope: Scope.Global,
                serviceType: ServiceType.marketplace,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.marketplace, Scope.Global, isStandAlone),
                isLongNameProduct: true,
                docId: 'marketplace',
            },
            {
                id: SERVICE_IDS.reinfer,
                name: 'Re:infer',
                svg: 'reinfer',
                scope: Scope.Tenant,
                serviceType: ServiceType.reinfer,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.reinfer, Scope.Tenant, isStandAlone),
                docId: 'reinfer',
            },
            {
                id: SERVICE_IDS.testmanager,
                name: 'Test Manager',
                svg: 'testmanager',
                scope: Scope.Tenant,
                serviceType: ServiceType.testManager,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.testManager, Scope.Tenant, isStandAlone),
                docId: 'test-suite',
            },
            {
                id: SERVICE_IDS.integrations,
                name: 'Integration Service',
                svg: 'integrations',
                scope: Scope.Tenant,
                serviceType: ServiceType.integrations,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.integrations, Scope.Tenant, isStandAlone),
                isLongNameProduct: true,
                docId: 'integrations',
            },
            {
                id: SERVICE_IDS.dataservice,
                name: 'Data Service',
                svg: 'dataservice',
                scope: Scope.Tenant,
                serviceType: ServiceType.dataservice,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.dataservice, Scope.Tenant, isStandAlone),
                docId: 'data-service',
            },
            {
                id: SERVICE_IDS.taskmining,
                name: 'Task Mining',
                svg: 'taskmining',
                scope: Scope.Tenant,
                serviceType: ServiceType.taskMining,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.taskMining, Scope.Tenant, isStandAlone),
                docId: 'task-mining',
            },
            {
                id: SERVICE_IDS.processmining,
                name: 'Process Mining',
                svg: 'processmining',
                scope: Scope.Tenant,
                serviceType: ServiceType.processMining,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.processMining, Scope.Tenant, isStandAlone),
                docId: 'process-mining',
            },
            {
                id: SERVICE_IDS.studio,
                name: 'Studio',
                svg: 'studio',
                scope: Scope.Global,
                serviceType: ServiceType.studio,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.studio, Scope.Global, isStandAlone),
                docId: 'studio',
            },
            {
                id: SERVICE_IDS.roboticsops,
                name: 'Automation Ops',
                svg: 'roboticsops',
                scope: Scope.Global,
                serviceType: ServiceType.roboticsOps,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.roboticsOps, Scope.Global, isStandAlone),
                isLongNameProduct: true,
                docId: 'automation-ops',
            },
            {
                id: SERVICE_IDS.du,
                name: 'Document Understanding',
                svg: 'du',
                scope: Scope.Tenant,
                serviceType: ServiceType.du,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.du, Scope.Tenant, isStandAlone),
                isSuperLongNameProduct: true,
                docId: 'document-understanding',
            },
            {
                id: SERVICE_IDS.aifabric,
                name: 'AI Center',
                svg: 'aifabric',
                scope: Scope.Tenant,
                serviceType: ServiceType.aifabric,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.aifabric, Scope.Tenant, isStandAlone),
                docId: 'ai-fabric',
            },
            {
                id: SERVICE_IDS.admin,
                name: t('admin', locale),
                svg: 'admin',
                scope: Scope.Global,
                serviceType: ServiceType.cloudRpa,
                url: `${getScopedUrl(standardUrl, tenantName, ServiceType.cloudRpa, Scope.Global, isStandAlone)}/${
                    isHost ? 'organizations' : localStorage.getItem('adminRevamp') === 'true' ? 'admin' : 'serviceinstances'
                }`,
                showInHostAccount: true,
            },
            {
                id: SERVICE_IDS.automationstore,
                name: 'Automation Store',
                svg: 'automationstore',
                scope: Scope.Tenant,
                serviceType: ServiceType.automationStore,
                url: getScopedUrl(standardUrl, tenantName, ServiceType.automationStore, Scope.Tenant, isStandAlone),
                isSetting: true,
                isLongNameProduct: true,
            },
            {
                id: SERVICE_IDS.help,
                name: t('help', locale),
                svg: 'help',
                scope: Scope.Global,
                serviceType: ServiceType.cloudRpa,
                url: `${getScopedUrl(
                    standardUrl,
                    tenantName,
                    ServiceType.cloudRpa,
                    Scope.Global,
                    isStandAlone,
                )}/resourcecenter`,
                isSetting: true,
            },
        ],
    };
}

export const getMoreTileProps = (locale: string): Service => ({
    id: 'more',
    name: t('more', locale),
    svg: 'more',
    scope: Scope.Global,
    serviceType: ServiceType.cloudRpa,
    url: '',
});
